import React, { memo, useMemo, useEffect, useCallback } from 'react';
import { TextAssetResponse } from '@goldcast/api/content';
import { doesClipHavePlayerConfig, isClipInvalid } from '@goldcast/player-config/src/utils/clipUtils';
import ClipPlayerWrapper from '@/Pages/Clip/ClipPlayer/ClipPlayerWrapper';
import PostClipWrapper from '@/Pages/PostsListPage/components/PostClipWrapper';
import { useAppContext } from '@/context/AppContext/AppContext';
import usePost from '@/hooks/usePost';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { createPostTextFromClipObject } from '@/hooks/utils/postHookUtil';
import { ClipAssetMetadata } from '@/stores/postStore';
import { createContentClip } from '@/libs/clipContentUtil';
import useAnalytics from '@/hooks/useAnalytics';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import ClipPlayerV2 from '@/App/remotion/MainPlayer/ClipPlayerV2';
import { SharedVideoTimedOut } from '@/context/ClipsContext/PlayerBlockers/SharedVideoTimedOut';
import { useSharedPost } from '@/Pages/Shared/SharedPostPage/SharedPostContext';
import {
  SharedPostPageAuthState,
  useSharedPostPageAuthHandler
} from '@/Pages/Shared/SharedPostPage/useSharedPostPageAuthHandler';
import { getClipAnalyticsPayloadV2 } from '@/hooks/utils/analyticsHookUtil';

/**
 * Used To Render Clips of Blog outside of Content lab (Shared Blog page and Smart Event Page)
 */
const SharedBlogClipPlayer = ({ post }: { post: TextAssetResponse | null }) => {
  const { clipData } = useClipsContext();
  const { authState, handleEditInContentLabButtonClick } = useSharedPostPageAuthHandler({ post });
  const isSignedIn = authState === SharedPostPageAuthState.SIGNED_IN;

  const isSmartEventsPage = isSignedIn && window.location.pathname.includes('/webinar');

  const isClipAvailable = useMemo(() => {
    return doesClipHavePlayerConfig(clipData);
  }, [clipData]);

  const customProperties = useMemo(() => {
    if (isClipInvalid(clipData)) {
      return null;
    }
    return {
      isSmartEventsPage,
      isSharedPage: true,
      isSignedIn,
      clipProperties: getClipAnalyticsPayloadV2(clipData),
      contentType: 'BLOG'
    };
  }, [isSmartEventsPage, isSignedIn, clipData]);

  return isClipAvailable ? (
    <ClipPlayerV2 showThumbnailOnMount isSharedPage={true} canEditCaptions={false} canEditSpeakerLabel={false} />
  ) : (
    <SharedVideoTimedOut
      customHeader={isSmartEventsPage ? 'Video preview unavailable' : undefined}
      customMessage={
        isSmartEventsPage ? 'Please open this video in Content Lab at least once to enable the preview.' : undefined
      }
      isSignedIn={isSignedIn}
      onClickOpenInContentLab={handleEditInContentLabButtonClick}
      customProperties={customProperties}
    />
  );
};

function PostPlayerWrapper({
  blockId,
  postText,
  onRemoveClip,
  onUpdatedPostText,
  previousBlockText
}: {
  blockId: string;
  postText: string;
  onRemoveClip: () => void;
  previousBlockText: string;
  onUpdatedPostText: (updatedPostText: string) => void;
}) {
  const {
    openReplaceClipDialog,
    extractClipDetailsFromPostText,
    clips,
    onActiveClipChange,
    removeClipFromPost,
    updateClipDetails,
    getClipAssetIds,
    getSelectedClipId,
    createClipFromTextForSavedSearch,
    isReplaceClipDialogOpen,
    selectedBlockId,
    selectedPost
  } = usePost();
  const { logger } = useAppContext();
  const { trackContentCreated, trackContentEdited } = useAnalytics();

  const isSavedSearchPage = useMemo(() => window.location.pathname.includes('/search'), [window.location.pathname]);

  const clipDetails: ClipAssetMetadata = useMemo(() => clips[blockId] || null, [clips[blockId]]);
  // Check if the post is shared post or used with in the studio
  const isSharedPost =
    window.location.pathname.includes('/shared/post') || window.location.pathname.includes('/webinar');
  const sharedPost = useSharedPost();

  useEffect(() => {
    extractClipDetailsFromPostText(blockId, postText);
  }, [blockId]);

  const clipAssetIds = useMemo(() => getClipAssetIds(clipDetails), [clipDetails, getClipAssetIds]);

  const selectedClipId = useMemo(() => getSelectedClipId(clipDetails), [clipDetails, getSelectedClipId]);

  const handleRemoveClip = useCallback(() => {
    onRemoveClip();
    removeClipFromPost(blockId);
    return Promise.resolve();
  }, [onRemoveClip, removeClipFromPost, blockId]);

  const handleActiveClipChange = useCallback(
    (index: number) => {
      const updatedPostText = onActiveClipChange(blockId, index);
      onUpdatedPostText(updatedPostText);
    },
    [onActiveClipChange, blockId, onUpdatedPostText]
  );

  const trackClipAction = (asset, source, state, isSuccess?) => {
    trackContentCreated({ asset, source, state, isMultimodal: true });
    if (isSuccess !== undefined) {
      trackContentEdited({ asset, isSuccess, updateType: 'RegenerateClip' });
    }
  };

  const onRegenerateClip = async () => {
    const { next_clip_info } = clipDetails;

    if (!selectedPost || !next_clip_info) {
      return Promise.resolve();
    }

    if (!next_clip_info.length) {
      showErrorToast('No more new clips found matching social post content.');
      return Promise.resolve();
    }

    try {
      trackClipAction(selectedPost, 'Regenerate', 'Started');

      const clipStartEndTime = next_clip_info[0];
      const newClip = await createContentClip(clipStartEndTime[0], clipStartEndTime[1], 'AI');

      const updatedClipDetails = {
        ...clipDetails,
        clip_asset_ids: [...clipDetails.clip_asset_ids, newClip.id],
        next_clip_info: next_clip_info.slice(1),
        activeClipIndex: clipDetails?.activeClipIndex + 1,
        currentClipIndex: clipDetails?.activeClipIndex + 1
      };
      const updatedPostText = updateClipDetails(blockId, updatedClipDetails);
      onUpdatedPostText(updatedPostText);
      EventBus.dispatch(CustomEvents.ReloadAllClips);
      showSuccessToast('Clip regenerated successfully');
      trackClipAction(selectedPost, 'Regenerate', 'Created', true);
      return Promise.resolve();
    } catch (error) {
      showErrorToast('Failed to regenerate clip');
      trackClipAction(selectedPost, 'Regenerate', 'Failed', false);
      return Promise.resolve();
    }
  };

  const onRegenerateClipForSavedSearch = useCallback(async () => {
    if (!selectedPost) {
      return Promise.resolve();
    }

    try {
      trackClipAction(selectedPost, 'Regenerate', 'Started');

      const clipAssetResponse = await createClipFromTextForSavedSearch(previousBlockText);

      const updatedClipDetails = {
        ...clipDetails,
        clip_asset_ids: [...clipDetails.clip_asset_ids, clipAssetResponse.id],
        activeClipIndex: clipDetails?.activeClipIndex + 1,
        currentClipIndex: clipDetails?.activeClipIndex + 1
      };

      const updatedPostText = updateClipDetails(blockId, updatedClipDetails);
      onUpdatedPostText(updatedPostText);

      EventBus.dispatch(CustomEvents.ReloadAllClips);
      showSuccessToast('Clip regenerated successfully');
      trackClipAction(selectedPost, 'Regenerate', 'Created', true);

      return Promise.resolve();
    } catch (error) {
      showErrorToast('Failed to regenerate clip');
      trackClipAction(selectedPost, 'Regenerate', 'Failed', false);

      return Promise.resolve();
    }
  }, [selectedPost, clipDetails, blockId, previousBlockText, onUpdatedPostText]);

  useEffect(() => {
    if (!isReplaceClipDialogOpen && selectedBlockId === blockId) {
      const updatedPostText = createPostTextFromClipObject(clipDetails);
      onUpdatedPostText(updatedPostText);
    }
  }, [isReplaceClipDialogOpen, selectedBlockId, blockId, clipDetails]);

  const handleTranscriptError = useCallback(() => {
    logger.error('[PostPlayerWrapper] Failed to load clip transcript', {
      clipId: selectedClipId,
      textAssetId: selectedPost?.id
    });
  }, [logger, selectedClipId, selectedPost?.id]);

  const replaceClip = () => {
    openReplaceClipDialog(blockId);
  };

  if (!selectedClipId) {
    return null;
  }

  return (
    <div className="relative h-full">
      <ClipPlayerWrapper
        clipId={selectedClipId}
        handleTranscriptError={handleTranscriptError}
        shared={isSharedPost}
        parentContext="PostPlayerWrapper"
        isSavedSearchPage={isSavedSearchPage}
      >
        {!isSharedPost ? (
          <PostClipWrapper
            openReplaceClipDialog={replaceClip}
            onRemoveClip={handleRemoveClip}
            onRegenerateClip={isSavedSearchPage ? onRegenerateClipForSavedSearch : onRegenerateClip}
            activeClip={clipDetails?.currentClipIndex || 0}
            clipAssetIds={clipAssetIds}
            handleActiveClipChange={handleActiveClipChange}
          />
        ) : (
          <SharedBlogClipPlayer post={sharedPost} />
        )}
      </ClipPlayerWrapper>
    </div>
  );
}

export default memo(PostPlayerWrapper);
